import {
  postLogin,
  postRegister,
  postResetPassword,
  postSendCode,
  postSignupCheck,
  postVerifyCode,
} from 'apis/auth';
import { BaseButton } from 'components/ui/BaseButton';
import { countryOptions } from 'constants/countryOption';
import { useDebounce } from 'hooks/useDebounce';
import { CommonHeader } from 'layouts/HeaderLayout';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { customerState, isAuthenticatedState, isMenuOpenState, shopState } from 'store/atoms';
import { useTranslation } from 'i18n';
import {
  Overlay,
  OverlayInner,
  LoginContainer,
  PageContainer,
  LoginFormContainer,
  Title,
  FormSection,
  Label,
  PhoneInputContainer,
  PhoneNumberRow,
  CountrySelectWrapper,
  CountrySelect,
  DropdownList,
  DropdownItem,
  PhoneInput,
  FullWidthInput,
  SendCodeButton,
  CodeInputContainer,
  CodeInput,
  Timer,
  VerifyButton,
  CheckboxContainer,
  Checkbox,
  CheckboxLabel,
  StepContainer,
  ButtonContainer,
  ForgotPasswordContainer,
  ForgotPasswordButton,
} from './LoginForm.styles';

interface LoginFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LoginForm = ({ isOpen, onClose }: LoginFormProps) => {
  const { t } = useTranslation();
  const shop = useRecoilValue(shopState);
  const setIsAuthenticated = useSetRecoilState(isAuthenticatedState);
  const setCustomer = useSetRecoilState(customerState);
  const setIsMenuOpen = useSetRecoilState(isMenuOpenState);
  const navigate = useNavigate();

  // 로그인 폼이 열릴 때 메뉴 닫기
  useEffect(() => {
    if (isOpen) {
      setIsMenuOpen(false);
    }
  }, [isOpen, setIsMenuOpen]);

  // 로그인 폼이 닫힐 때 form 초기화
  useEffect(() => {
    if (!isOpen) {
      // Form 상태 초기화
      setStep('phone');
      setPhoneNumber('');
      setPassword('');
      setConfirmPassword('');
      setVerificationCode('');
      setName('');
      setAgreeTerms(false);
      setCodeSent(false);
      setPhoneVerified(false);
      setCodeVerified(false);
      setTimer(0);
      setLoading(false);
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  // Form state
  const [regionCode, setRegionCode] = useState('US');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  // Flow state
  const [step, setStep] = useState<
    'phone' | 'login' | 'signup' | 'verify' | 'reset' | 'resetVerify'
  >('phone');
  const [loading, setLoading] = useState(false);

  const [codeSent, setCodeSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [timer, setTimer] = useState(0);
  const [agreeTerms, setAgreeTerms] = useState(false);

  // UI state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const debouncedPhone = useDebounce(phoneNumber, 500);

  // 사이드바가 열릴 때 body 스크롤 비활성화, 닫힐 때 활성화
  useEffect(() => {
    if (isOpen) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // 스크롤 위치 복원
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // 컴포넌트 언마운트 시 스타일 초기화
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // 전화번호 검증 및 분기 처리
  useEffect(() => {
    const checkPhone = async () => {
      if (debouncedPhone && debouncedPhone.length >= 10) {
        const payload = {
          shopSeqNo: shop?.shopSeqNo,
          phone: debouncedPhone || '',
          countryCode: regionCode,
        };
        try {
          const result = await postSignupCheck(payload);

          if (!result.data) {
            setStep('signup');
          } else {
            setStep('login');
          }
        } catch (error) {
          console.error('Phone check error:', error);
        }
      } else if (debouncedPhone.length === 0) {
        setStep('phone');
        setCodeSent(false);
        setTimer(0);
      }
    };

    checkPhone();
  }, [debouncedPhone, regionCode, shop?.shopSeqNo]);

  const selectedCountry =
    countryOptions.find((option) => option.value === regionCode) || countryOptions[0];

  const handleCountrySelect = (value: string) => {
    setRegionCode(value);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSendCode = async () => {
    if (phoneNumber.trim().length > 0) {
      setCodeSent(true);
      setPhoneVerified(true);
      setCodeVerified(false);
      setTimer(180); // 3분 타이머

      try {
        const payload = {
          phone: phoneNumber || '',
          countryCode: regionCode,
        };
        await postSendCode(payload);
      } catch (error) {
        console.error('Send code error:', error);
        alert(t('auth.alert.code_send_failed'));
        setPhoneVerified(false);
      }
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.trim().length === 0) {
      alert(t('auth.alert.enter_code'));
      return;
    }

    try {
      const verifyPayload = {
        phone: phoneNumber || '',
        authCode: verificationCode,
      };
      const verifyResponse = await postVerifyCode(verifyPayload);

      if (verifyResponse?.data?.result) {
        setCodeVerified(true);
        alert(t('auth.alert.code_verified'));
        setTimer(0);
        // reset step이면 reset으로, 아니면 verify로
        if (step === 'reset') {
          setStep('resetVerify');
        } else {
          setStep('verify');
        }
      } else {
        alert(t('auth.alert.invalid_code'));
        setTimer(0);
      }
    } catch (error) {
      console.error('Verify code error:', error);
      alert(t('auth.alert.verify_failed'));
    }
  };

  // Timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSubmit = async () => {
    if (step === 'phone') return;

    setLoading(true);

    try {
      if (step === 'login') {
        const payload = {
          shopSeqNo: shop?.shopSeqNo || 0,
          phone: phoneNumber || '',
          password,
          countryCode: regionCode,
        };
        const response = await postLogin(payload);

        setCustomer(response);

        if (response.authToken) {
          setIsAuthenticated(true);
          localStorage.setItem('customer_token', response.authToken);
          onClose();
        } else {
          alert(t('auth.alert.login_failed'));
          setPassword('');
        }
      } else if (step === 'signup' || step === 'verify') {
        const payload = {
          shopSeqNo: shop?.shopSeqNo || 0,
          phone: phoneNumber || '',
          countryCode: regionCode,
          password,
          name,
        };
        const response = await postRegister(payload);

        setCustomer(response);

        if (response.authToken) {
          setIsAuthenticated(true);
          localStorage.setItem('customer_token', response.authToken);
          onClose();
          navigate('/');
        } else {
          alert(t('auth.alert.signup_failed'));
        }
      } else if (step === 'reset' || step === 'resetVerify') {
        const payload = {
          shopSeqNo: shop?.shopSeqNo || 0,
          phone: phoneNumber || '',
          countryCode: regionCode,
          newPassword: password,
        };
        const response = await postResetPassword(payload);

        if (response?.message === 'OK') {
          alert(t('auth.alert.password_reset_success'));
          onClose();
        } else {
          alert(t('auth.alert.password_reset_failed'));
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (loading) {
      if (step === 'login') return t('auth.cta.logging_in');
      if (step === 'reset' || step === 'resetVerify') return t('auth.cta.resetting');
      return t('auth.cta.signing_up');
    }
    if (step === 'phone') return t('auth.cta.login_or_signup');
    if (step === 'login') return t('auth.cta.login');
    if (step === 'reset' || step === 'resetVerify') return t('payment.cta.confirm');
    return t('auth.cta.create_account');
  };

  const isFormValid = () => {
    if (step === 'phone') {
      return phoneNumber.trim().length >= 10;
    }
    if (step === 'login') {
      return phoneNumber.trim().length >= 10 && password.length > 0;
    }
    if (step === 'signup' || step === 'verify') {
      return (
        phoneNumber.trim().length >= 10 &&
        verificationCode.trim().length > 0 &&
        codeVerified &&
        password.length >= 1 &&
        agreeTerms
      );
    }
    if (step === 'reset' || step === 'resetVerify') {
      return (
        phoneNumber.trim().length >= 10 &&
        verificationCode.trim().length > 0 &&
        codeVerified &&
        password.length >= 1 &&
        confirmPassword.length >= 1 &&
        password === confirmPassword
      );
    }
    return false;
  };

  const closeLoginForm = () => {
    onClose();
  };

  return (
    <Overlay $isOpen={isOpen} onClick={closeLoginForm}>
      <OverlayInner onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
        <LoginContainer $isOpen={isOpen}>
          <CommonHeader />
          <PageContainer>
            <Title>
              {step === 'reset' || step === 'resetVerify' ? t('auth.title.reset_password') : t('auth.title.login')}
            </Title>
            <LoginFormContainer>
              <FormSection>
                <Label>{t('auth.label.phone_number')}</Label>
                <PhoneInputContainer>
                  <CountrySelectWrapper data-dropdown>
                    <CountrySelect
                      $disabled={phoneVerified}
                      onClick={phoneVerified ? undefined : toggleDropdown}
                    >
                      {selectedCountry.label}
                    </CountrySelect>
                    {!phoneVerified && (
                      <DropdownList $isOpen={isDropdownOpen}>
                        {countryOptions.map((option) => (
                          <DropdownItem
                            key={option.value}
                            $selected={option.value === regionCode}
                            onClick={() => handleCountrySelect(option.value)}
                          >
                            {option.label}
                          </DropdownItem>
                        ))}
                      </DropdownList>
                    )}
                  </CountrySelectWrapper>

                  {/* 기본 Phone Number 입력 (모든 step에서 보임) */}
                  {step === 'phone' || step === 'login' ? (
                    <FullWidthInput
                      type="tel"
                      placeholder={t('auth.placeholder.phone_number')}
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      $disabled={codeVerified}
                      disabled={codeVerified}
                    />
                  ) : (
                    <PhoneNumberRow>
                      <PhoneInput
                        type="tel"
                        placeholder={t('auth.placeholder.phone_number')}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        $disabled={phoneVerified}
                        disabled={phoneVerified}
                      />
                      <SendCodeButton
                        onClick={handleSendCode}
                        disabled={
                          phoneNumber.trim().length === 0 ||
                          (codeSent && timer > 0) ||
                          phoneVerified
                        }
                      >
                        {t('auth.cta.send_code')}
                      </SendCodeButton>
                    </PhoneNumberRow>
                  )}

                  {/* Code 입력 (회원가입 및 비밀번호 재설정) */}
                  {(step === 'verify' ||
                    step === 'signup' ||
                    step === 'reset' ||
                    step === 'resetVerify') &&
                    codeSent && (
                      <CodeInputContainer>
                        <CodeInput
                          type="text"
                          maxLength={6}
                          placeholder={t('auth.placeholder.code_number')}
                          value={verificationCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, ''); // 숫자만 허용
                            if (value.length <= 6) {
                              setVerificationCode(value);
                            }
                          }}
                          $disabled={codeVerified}
                          disabled={codeVerified}
                        />
                        {timer > 0 && <Timer>{formatTime(timer)}</Timer>}
                      </CodeInputContainer>
                    )}

                  {/* Verify Code 버튼 (회원가입 및 비밀번호 재설정) */}
                  {(step === 'verify' ||
                    step === 'signup' ||
                    step === 'reset' ||
                    step === 'resetVerify') &&
                    codeSent && (
                      <VerifyButton
                        $disabled={verificationCode.trim().length === 0 || codeVerified}
                        onClick={handleVerifyCode}
                        disabled={codeVerified}
                      >
                        {t('auth.cta.verify_code')}
                      </VerifyButton>
                    )}
                </PhoneInputContainer>
              </FormSection>

              {/* 로그인 Step - 기존 회원용 */}
              <StepContainer $show={step === 'login'}>
                <Label style={{ marginTop: '24px' }}>{t('auth.label.password')}</Label>
                <FullWidthInput
                  type="password"
                  placeholder={t('auth.placeholder.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <ForgotPasswordContainer>
                  <ForgotPasswordButton
                    onClick={() => {
                      setStep('reset');
                      setCodeSent(false);
                      setCodeVerified(false);
                      setPhoneVerified(false);
                      setPassword('');
                      setConfirmPassword('');
                    }}
                  >
                    {t('auth.cta.forgot_password')}
                  </ForgotPasswordButton>
                </ForgotPasswordContainer>
              </StepContainer>

              {/* 회원가입 Step - 신규 회원용 */}
              <StepContainer $show={step === 'verify'}>
                <Label style={{ marginTop: '24px' }}>{t('auth.label.your_name')}</Label>
                <FullWidthInput
                  type="text"
                  placeholder={t('auth.placeholder.full_name')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  $disabled={!codeVerified}
                  disabled={!codeVerified}
                />
                <Label style={{ marginTop: '16px' }}>{t('auth.label.password')}</Label>
                <FullWidthInput
                  type="password"
                  placeholder={t('auth.placeholder.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  $disabled={!codeVerified}
                  disabled={!codeVerified}
                />

                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    disabled={!codeVerified}
                  />
                  <CheckboxLabel>
                    {t('payment.agree_terms_prefix')}{' '}
                    <a
                      href="https://kimcaddie.notion.site/kaddie-terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('payment.terms_link')}
                    </a>{' '}
                    {t('payment.agree_terms_and')}{' '}
                    <a
                      href="https://kimcaddie.notion.site/kaddie-privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('payment.privacy_policy_link')}
                    </a>
                  </CheckboxLabel>
                </CheckboxContainer>
              </StepContainer>

              {/* 비밀번호 재설정 Step */}
              <StepContainer $show={step === 'resetVerify'}>
                <Label style={{ marginTop: '24px' }}>{t('auth.label.new_password')}</Label>
                <FullWidthInput
                  type="password"
                  placeholder={t('auth.placeholder.new_password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  $disabled={!codeVerified}
                  disabled={!codeVerified}
                />
                <Label style={{ marginTop: '16px' }}>{t('auth.label.confirm_password')}</Label>
                <FullWidthInput
                  type="password"
                  placeholder={t('auth.placeholder.confirm_password')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  $disabled={!codeVerified}
                  disabled={!codeVerified}
                />
              </StepContainer>
            </LoginFormContainer>
          </PageContainer>
          <ButtonContainer>
            <BaseButton
              text={getButtonText()}
              variant="primary"
              size="large"
              onClick={isFormValid() && !loading ? handleSubmit : undefined}
              disabled={!isFormValid() || loading}
            />
          </ButtonContainer>
        </LoginContainer>
      </OverlayInner>
    </Overlay>
  );
};
